// Client/src/utils/stateLrs/massdot.js
// MassDOT (Massachusetts DOT) LRS adapter. Data-source facts verified live
// 2026-07-26 — see memory/reference_massdot_lrs_road_inventory_api.md.
//
// Source: the Road Inventory year-end FeatureServer. Layer 11 is the 2025
// inventory; layers 0-10 are 2014-2024. The YEAR IS THE LAYER, so unlike ALDOT
// there is no temporal (`ToDate IS NULL`) filter to apply — pointing at layer 11
// already means "current".
//
// One layer carries the whole network (504,907 segments); `Route_System`
// classifies it. I/US/SR are the numbered routes that carry posted mileposts;
// L/N/M/R/B are local roads, ramps and paths that have measures but no signs.
// We map that split onto the same milepost/milepoint networks the ALDOT adapter
// uses, so shared consumers (formatLrsLocation, lane-points enrichment) keep
// working unchanged.

import {
  measureRangesFromSections,
  interpolatePointAtMeasure,
  projectPointToMeasure,
  smoothRouteRuns,
} from './lrsMath';
import { MA_COUNTY_NAMES, MA_CITY_NAMES, maDistrictLabel } from './maPlaces';

// Route_System coded-value domain (dRouteSystem_10), verbatim from the layer.
export const ROUTE_SYSTEM_LABEL = {
  I: 'I', US: 'US', SR: 'SR', R: 'Ramp',
  L: 'Local', M: 'Misc', N: 'Road', B: 'Bike/Ped',
};

// Only these carry field-posted mileposts. Everything else has a measure but
// nothing signed on the roadside.
const NUMBERED_SYSTEMS = new Set(['I', 'US', 'SR']);

// Speed_Lim sentinel for "not posted / unknown". A real limit is never 999.
const SPEED_LIMIT_UNKNOWN = 999;

/** Directions whose measures run OPPOSITE to the field-posted mileposts.
 *  Verified against the Exits layer (MA renumbered exits to milepost-based in
 *  2021): on NB/EB the LRS measure IS the posted milepost (mean deviation
 *  0.32-0.44 mi over 24-71 exits); on SB/WB the posted value is
 *  `routeLength - measure` (mean deviation 0.38-0.50 mi). */
const REVERSED_DIRECTIONS = new Set(['SB', 'WB']);

export function isReversedDirection(routeDirection) {
  return REVERSED_DIRECTIONS.has(String(routeDirection ?? '').toUpperCase());
}

export function isNumberedSystem(routeSystem) {
  return NUMBERED_SYSTEMS.has(String(routeSystem ?? '').toUpperCase());
}

// ─── Posted-milepost conversion ───────────────────────────────────────────────

/** LRS measure → field-posted milepost. Returns null when the route length is
 *  unknown or the route carries no posted mileposts, so callers fall back to
 *  showing the raw measure rather than inventing a number. */
export function toPostedMilepost({ measure, routeDirection, routeLength, routeSystem }) {
  if (!Number.isFinite(measure)) return null;
  if (routeSystem != null && !isNumberedSystem(routeSystem)) return null;
  if (!isReversedDirection(routeDirection)) return measure;
  return Number.isFinite(routeLength) ? routeLength - measure : null;
}

/** Field-posted milepost → LRS measure (the inverse; the picker takes posted
 *  input and must query the service in measure space). */
export function fromPostedMilepost({ posted, routeDirection, routeLength }) {
  if (!Number.isFinite(posted)) return null;
  if (!isReversedDirection(routeDirection)) return posted;
  return Number.isFinite(routeLength) ? routeLength - posted : null;
}

// ─── Route keys ───────────────────────────────────────────────────────────────
// `route_id` values contain spaces ("I93 SB") and hyphens ("I190-DUP NB"), so
// the key is split on the FIRST colon only — never on '-' the way the ALDOT
// key parser does.

export function routeKeyFor(routeId, routeSystem) {
  const network = isNumberedSystem(routeSystem) ? 'milepost' : 'milepoint';
  return `${network}:${routeId}`;
}

export function parseRouteKey(key) {
  const s = String(key ?? '');
  const i = s.indexOf(':');
  if (i < 0) return { network: 'milepost', routeId: s };
  return { network: s.slice(0, i), routeId: s.slice(i + 1) };
}

// ─── Zones ────────────────────────────────────────────────────────────────────

/** Massachusetts has no measure-reset zone (measures run the length of each
 *  route), so the zone is purely descriptive: municipality + county, with the
 *  MassDOT highway district as the region analogue. */
export function buildZone({ countyCode, cityCode, hwyDist }) {
  const county = MA_COUNTY_NAMES[String(countyCode)] ?? null;
  const city = MA_CITY_NAMES[String(cityCode)] ?? null;
  if (!county && !city) return null;
  const district = maDistrictLabel(hwyDist);
  return {
    kind: 'municipality',
    code: cityCode != null ? String(cityCode) : null,
    name: city ?? county,
    county,
    region: district,
    // Consumed verbatim by formatLrsLocation so we get "· MassDOT District 6"
    // instead of the ALDOT-shaped "· East Central Region".
    regionLabel: district,
  };
}

// ─── Normalization ────────────────────────────────────────────────────────────

/** ArcGIS query JSON → NormalizedSection[]. ~0.6 % of features come back with
 *  null geometry; they normalize to an empty `paths` and are skipped by the
 *  math rather than dropped here, matching the ALDOT adapter's behaviour. */
export function normalizeMassdotSections(arcgisJson) {
  return (arcgisJson?.features ?? []).map((f) => {
    const a = f.attributes ?? {};
    const speed = Number(a.Speed_Lim);
    return {
      routeId: a.route_id ?? null,
      routeSystem: a.Route_System ?? null,
      routeNumber: a.Route_Number ?? null,
      routeDirection: a.Route_Direction ?? null,
      streetName: a.St_Name ?? null,
      cityCode: a.City ?? null,
      countyCode: a.County ?? null,
      hwyDist: a.Hwy_Dist ?? null,
      speedLimit: Number.isFinite(speed) && speed !== SPEED_LIMIT_UNKNOWN ? speed : null,
      fromMeasure: Number.isFinite(Number(a.from_measure)) ? Number(a.from_measure) : null,
      toMeasure: Number.isFinite(Number(a.to_measure)) ? Number(a.to_measure) : null,
      paths: f.geometry?.paths ?? [],
    };
  });
}

// ─── Labels ───────────────────────────────────────────────────────────────────

/** "I-93 SB", "SR-2 EB", or the street name for unnumbered roads. */
export function routeLabelFor({ routeId, routeSystem, routeNumber, routeDirection, streetName, cityCode }) {
  const dir = routeDirection ? ` ${routeDirection}` : '';
  if (isNumberedSystem(routeSystem)) {
    const sys = ROUTE_SYSTEM_LABEL[routeSystem] ?? routeSystem;
    return `${sys}-${routeNumber}${dir}`;
  }
  const city = MA_CITY_NAMES[String(cityCode)];
  const name = streetName ? titleCase(streetName) : (routeId ?? 'Unnamed road');
  return city ? `${name} (${city})${dir}` : `${name}${dir}`;
}

/** MassDOT stores street names SHOUTING ("JOHN F FITZGERALD EXPRESSWAY"). */
export function titleCase(s) {
  return String(s ?? '')
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

// ─── Fetch layer / adapter ────────────────────────────────────────────────────

const FEATURESERVER =
  'https://gis.massdot.state.ma.us/arcgis/rest/services/Roads/RoadInventoryYearEndFiles/FeatureServer';
// Layer 11 = Road Inventory 2025. Bump this (and RE-VERIFY the field list) when
// MassDOT publishes a new year-end layer; older years stay addressable at 0-10.
const LAYER_INVENTORY = 11;
export const INVENTORY_YEAR = 2025;

const FETCH_TIMEOUT_MS = 15000;
// One transient burst of HTTP-200-with-error-body failures was observed on
// 2026-07-26 (four consecutive queries failed, then 30/30 succeeded unchanged).
// The ALDOT adapter has no fetch-level retry and surfaces such blips straight to
// the user; here a single cheap retry absorbs them.
const FETCH_RETRIES = 1;
const RETRY_DELAY_MS = 400;

const LOCAL_SEARCH_LIMIT = 50;
// Attribute rows scanned for a local search before client-side dedupe. Local
// roads are segmented per attribute change, so one street is many rows.
const LOCAL_SEARCH_SCAN = 400;
const LOCATE_RADIUS_METERS = 150;

const GEOMETRY_FIELDS =
  'route_id,from_measure,to_measure,Route_System,Route_Number,Route_Direction,St_Name,City,County,Hwy_Dist,Speed_Lim';

export class LrsUnavailableError extends Error {
  constructor(message, detail = null) {
    super(message);
    this.name = 'LrsUnavailableError';
    this.detail = detail;
  }
}

function buildQueryUrl(layerId, params) {
  const qs = new URLSearchParams({ f: 'json', ...params });
  return `${FEATURESERVER}/${layerId}/query?${qs.toString()}`;
}

/** Escape a single-quoted SQL literal for an ArcGIS WHERE clause. */
function sqlLiteral(value) {
  return String(value ?? '').replace(/'/g, "''");
}

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export function createMassdotAdapter({ fetchImpl } = {}) {
  const doFetch = fetchImpl ?? ((...args) => fetch(...args));
  const geometryCache = new Map();  // routeKey → { sections, mRanges, routeLength }
  let statewideCache = null;        // RouteEntry[]
  let statewidePromise = null;      // in-flight guard so parallel callers share one fetch
  const routeLengths = new Map();   // route_id → total miles (numbered routes)
  const locateCache = new Map();    // "lat,lng"@5dp → Promise<LrsPointLocation|null>
  let sourceInfo = { statewideSource: null, snapshotDate: null };

  async function fetchJsonOnce(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await doFetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw new LrsUnavailableError(
          'The road data service is unavailable right now.',
          `HTTP ${res.status} from ${url}`,
        );
      }
      const body = await res.json();
      // ArcGIS reports failures as HTTP 200 with an error body.
      if (body && body.error) {
        throw new LrsUnavailableError(
          'The road data service returned an error.',
          `ArcGIS error ${body.error.code}: ${body.error.message}`,
        );
      }
      return body;
    } catch (err) {
      if (err instanceof LrsUnavailableError) throw err;
      throw new LrsUnavailableError(
        'Could not reach the road data service (network problem or timeout).',
        err?.message ?? null,
      );
    } finally {
      clearTimeout(timer);
    }
  }

  async function fetchJson(url) {
    let lastErr = null;
    for (let attempt = 0; attempt <= FETCH_RETRIES; attempt += 1) {
      try {
        return await fetchJsonOnce(url);
      } catch (err) {
        lastErr = err;
        if (attempt < FETCH_RETRIES) await delay(RETRY_DELAY_MS);
      }
    }
    throw lastErr;
  }

  /** Layer query with resultOffset pagination. maxRecordCount is 2,000,000 and
   *  the statewide numbered network (32k features) comes back in one page — the
   *  loop is a safety net for a server-side cap change. */
  async function fetchAllFeatures(params) {
    const features = [];
    let offset = 0;
    for (;;) {
      const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
        ...params, resultOffset: String(offset),
      }));
      features.push(...(body.features ?? []));
      if (!body.exceededTransferLimit || !body.features?.length) break;
      offset += body.features.length;
    }
    return { features };
  }

  /** The route picker's whole payload: one server-side groupBy returning 314
   *  numbered routes with their measure extents (~45 KB). No geometry is pulled
   *  until a route is actually selected, which is why this adapter needs no
   *  S3 snapshot the way the ALDOT one does. */
  async function fetchRouteIndex() {
    const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
      where: "Route_System IN ('I','US','SR')",
      groupByFieldsForStatistics: 'route_id,Route_System,Route_Number,Route_Direction',
      outStatistics: JSON.stringify([
        { statisticType: 'min', onStatisticField: 'from_measure', outStatisticFieldName: 'mp_lo' },
        { statisticType: 'max', onStatisticField: 'to_measure', outStatisticFieldName: 'mp_hi' },
      ]),
      orderByFields: 'route_id',
    }));
    return (body.features ?? []).map((f) => {
      const a = f.attributes ?? {};
      const lo = Number(a.mp_lo);
      const hi = Number(a.mp_hi);
      const routeLength = Number.isFinite(hi) ? hi : null;
      return {
        key: routeKeyFor(a.route_id, a.Route_System),
        network: 'milepost',
        routeId: a.route_id,
        label: routeLabelFor({
          routeId: a.route_id,
          routeSystem: a.Route_System,
          routeNumber: a.Route_Number,
          routeDirection: a.Route_Direction,
        }),
        routeType: a.Route_System,
        routeNumber: a.Route_Number,
        routeDirection: a.Route_Direction,
        countyCode: null,
        routeLength,
        reversed: isReversedDirection(a.Route_Direction),
        mRanges: Number.isFinite(lo) && Number.isFinite(hi) ? [[lo, hi]] : [],
      };
    });
  }

  /** Populate + cache the numbered-route index. Shared by listStatewideRoutes
   *  and by posted-milepost conversion (which needs each route's total length),
   *  so a map click can label a milepost without the Routes panel ever opening. */
  function ensureRouteIndex() {
    if (statewideCache) return Promise.resolve(statewideCache);
    if (!statewidePromise) {
      statewidePromise = fetchRouteIndex()
        .then((entries) => {
          for (const e of entries) {
            if (Number.isFinite(e.routeLength)) routeLengths.set(e.routeId, e.routeLength);
          }
          statewideCache = entries;
          sourceInfo = { statewideSource: 'live', snapshotDate: null };
          return entries;
        })
        .finally(() => { statewidePromise = null; });
    }
    return statewidePromise;
  }

  /** Route length in miles, or null if not a known numbered route. Never
   *  throws — posted-milepost display degrades to the raw measure. */
  async function routeLengthFor(routeId) {
    if (routeLengths.has(routeId)) return routeLengths.get(routeId);
    try {
      await ensureRouteIndex();
    } catch (_) { /* leave unknown */ }
    return routeLengths.has(routeId) ? routeLengths.get(routeId) : null;
  }

  async function fetchRouteSections(routeId) {
    const raw = await fetchAllFeatures({
      where: `route_id = '${sqlLiteral(routeId)}'`,
      outFields: GEOMETRY_FIELDS,
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    });
    return normalizeMassdotSections(raw);
  }

  async function getRouteGeometry(key) {
    if (geometryCache.has(key)) return geometryCache.get(key);
    const { routeId } = parseRouteKey(key);
    const sections = await fetchRouteSections(routeId);
    if (!sections.length) {
      throw new LrsUnavailableError(`No geometry found for route ${routeId}.`);
    }
    const mRanges = measureRangesFromSections(sections);
    const declared = sections
      .map((s) => s.toMeasure)
      .filter((m) => Number.isFinite(m));
    const routeLength = declared.length ? Math.max(...declared) : null;
    if (Number.isFinite(routeLength) && isNumberedSystem(sections[0]?.routeSystem)) {
      routeLengths.set(routeId, routeLength);
    }
    const result = { sections, mRanges, routeLength };
    geometryCache.set(key, result);
    return result;
  }

  async function querySectionsNear(point) {
    const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
      geometry: JSON.stringify({ x: point.lng, y: point.lat, spatialReference: { wkid: 4326 } }),
      geometryType: 'esriGeometryPoint',
      inSR: '4326',
      spatialRel: 'esriSpatialRelIntersects',
      distance: String(LOCATE_RADIUS_METERS),
      units: 'esriSRUnit_Meter',
      outFields: GEOMETRY_FIELDS,
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    }));
    return normalizeMassdotSections(body);
  }

  /** Nearest section to `point`, preferring numbered routes when they are
   *  within `numberedBiasMeters` of the closest hit. Dense interchanges return
   *  20+ ramps and service roads within 150 m (21 at the Zakim Bridge), so a
   *  pure nearest-wins rule labels a highway click with a ramp. */
  function nearestOnSections(sections, point, { numberedBias = 25 } = {}) {
    const hits = [];
    for (const s of sections) {
      const proj = projectPointToMeasure([s], point);
      if (!proj || proj.distanceMeters > LOCATE_RADIUS_METERS) continue;
      hits.push({ section: s, m: proj.m, distanceMeters: proj.distanceMeters });
    }
    if (!hits.length) return null;
    hits.sort((a, b) => a.distanceMeters - b.distanceMeters);
    const closest = hits[0];
    const preferred = hits.find(
      (h) => isNumberedSystem(h.section.routeSystem)
        && h.distanceMeters <= closest.distanceMeters + numberedBias,
    );
    return preferred ?? closest;
  }

  /** Same preference rule `nearestOnSections` applies between sections, lifted
   *  to the choice BETWEEN routes: nearest wins unless a numbered route sits
   *  within `numberedBias` of it. */
  function pickCandidate(perRoute, { numberedBias = 25 } = {}) {
    if (!perRoute.length) return null;
    const sorted = [...perRoute].sort((a, b) => a.hit.distanceMeters - b.hit.distanceMeters);
    const closest = sorted[0];
    const preferred = sorted.find(
      (c) => isNumberedSystem(c.hit.section.routeSystem)
        && c.hit.distanceMeters <= closest.hit.distanceMeters + numberedBias,
    );
    return preferred ?? closest;
  }

  function locationFromHit(hit, routeLength) {
    const s = hit.section;
    const measure = hit.m;
    const posted = toPostedMilepost({
      measure,
      routeDirection: s.routeDirection,
      routeLength,
      routeSystem: s.routeSystem,
    });
    return {
      routeKey: routeKeyFor(s.routeId, s.routeSystem),
      routeLabel: routeLabelFor(s),
      network: isNumberedSystem(s.routeSystem) ? 'milepost' : 'milepoint',
      measure,
      postedMilepost: posted,
      zone: buildZone(s),
      distanceMeters: hit.distanceMeters,
      speedLimit: s.speedLimit,
      streetName: s.streetName ? titleCase(s.streetName) : null,
    };
  }

  return {
    stateCode: 'MA',
    stateName: 'Massachusetts (MassDOT)',
    capabilities: {
      // Speed_Lim is populated on 91,962 of 504,907 segments (~18 %).
      speedLimits: true,
      // Measures on SB/WB routes run opposite to the posted mileposts, so the
      // UI must convert rather than print the raw value.
      postedMilepost: true,
      localRoadSearch: true,
    },
    inventoryYear: INVENTORY_YEAR,

    getSourceInfo() {
      return { ...sourceInfo };
    },

    async listStatewideRoutes() {
      return ensureRouteIndex();
    },

    /** Local-road search. MassDOT carries `St_Name`, which ALDOT does not, so
     *  this is a real name search rather than a coded-designation match. */
    async searchLocalRoutes(term) {
      const cleaned = String(term ?? '').trim();
      if (!cleaned) return [];
      const body = await fetchJson(buildQueryUrl(LAYER_INVENTORY, {
        where: buildLocalWhere(cleaned),
        outFields: 'route_id,St_Name,Route_System,Route_Number,Route_Direction,City,County,Hwy_Dist',
        returnGeometry: 'false',
        orderByFields: 'route_id',
        resultRecordCount: String(LOCAL_SEARCH_SCAN),
      }));
      const seen = new Set();
      const entries = [];
      for (const s of normalizeMassdotSections(body)) {
        const key = routeKeyFor(s.routeId, s.routeSystem);
        if (seen.has(key)) continue;
        seen.add(key);
        entries.push({
          key,
          network: isNumberedSystem(s.routeSystem) ? 'milepost' : 'milepoint',
          routeId: s.routeId,
          label: routeLabelFor(s),
          routeType: s.routeSystem,
          routeNumber: s.routeNumber,
          routeDirection: s.routeDirection,
          countyCode: s.countyCode != null ? String(s.countyCode) : null,
          zone: buildZone(s),
          reversed: isReversedDirection(s.routeDirection),
          routeLength: null, // unknown until geometry is fetched
          mRanges: null,
        });
        if (entries.length >= LOCAL_SEARCH_LIMIT) break;
      }
      return entries;
    },

    getRouteGeometry,

    /** Posted milepost → lat/lng. Input is in POSTED space (what the user
     *  reads off a sign); it is converted to LRS measure before interpolation. */
    async measureToPoint(key, posted) {
      const { sections, routeLength } = await getRouteGeometry(key);
      const direction = sections[0]?.routeDirection;
      const measure = fromPostedMilepost({ posted, routeDirection: direction, routeLength });
      if (!Number.isFinite(measure)) return null;
      return interpolatePointAtMeasure(sections, measure);
    },

    /** lat/lng → posted milepost (falls back to the raw measure on routes with
     *  no posted mileposts, so local roads still return a usable number). */
    async pointToMeasure(key, point) {
      const { sections, routeLength } = await getRouteGeometry(key);
      const r = projectPointToMeasure(sections, point);
      if (!r) return null;
      const posted = toPostedMilepost({
        measure: r.m,
        routeDirection: sections[0]?.routeDirection,
        routeLength,
        routeSystem: sections[0]?.routeSystem,
      });
      return posted ?? r.m;
    },

    /** Raw LRS measure for a point — the value MassDOT's own data uses. */
    async pointToRawMeasure(key, point) {
      const { sections } = await getRouteGeometry(key);
      const r = projectPointToMeasure(sections, point);
      return r ? r.m : null;
    },

    toPostedMilepost,
    fromPostedMilepost,

    /** Nearest road + measure within 150 m of a clicked point. Fire-and-forget
     *  safe: resolves null on any failure, caches (incl. in-flight) per ~1 m. */
    locatePoint(point) {
      if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
        return Promise.resolve(null);
      }
      const cacheKey = `${point.lat.toFixed(5)},${point.lng.toFixed(5)}`;
      if (locateCache.has(cacheKey)) return locateCache.get(cacheKey);
      const promise = (async () => {
        const sections = await querySectionsNear(point);
        const hit = nearestOnSections(sections, point);
        if (!hit) return null;
        const routeLength = isNumberedSystem(hit.section.routeSystem)
          ? await routeLengthFor(hit.section.routeId)
          : null;
        return locationFromHit(hit, routeLength);
      })().catch(() => null);
      locateCache.set(cacheKey, promise);
      return promise;
    },

    /** Per-point LRS enrichment for a lane's points — same contract as the
     *  ALDOT adapter: an array aligned 1:1 with `points`, each entry
     *  { road, milepost, county, region, distance_m } or null. `milepost` is the
     *  POSTED value; `lrs_measure` carries the raw MassDOT measure alongside it
     *  so downstream reports can cross-reference MassDOT's own data. */
    async enrichPointsWithLrs(routeKeys, points) {
      let keys = [...new Set((routeKeys || []).filter(Boolean))];
      const pts = Array.isArray(points) ? points : [];
      if (!pts.length) return [];

      if (!keys.length) {
        // No binding (manually drawn route): discover the nearest routes from a
        // few anchors instead of one spatial query per point.
        try {
          const anchorIdxs = [...new Set([0, Math.floor(pts.length / 2), pts.length - 1])];
          const found = new Set();
          for (const idx of anchorIdxs) {
            const pt = pts[idx];
            if (!pt) continue;
            const sections = await querySectionsNear(pt).catch(() => []);
            const hit = nearestOnSections(sections, pt);
            if (hit) found.add(routeKeyFor(hit.section.routeId, hit.section.routeSystem));
          }
          keys = [...found];
        } catch (_) {
          keys = [];
        }
      }
      if (!keys.length) return pts.map(() => null);

      const sectionSets = [];
      for (const key of keys) {
        try {
          const { sections, routeLength } = await getRouteGeometry(key);
          if (Array.isArray(sections) && sections.length) {
            sectionSets.push({ key, sections, routeLength });
          }
        } catch (_) { /* skip a key whose geometry can't be loaded */ }
      }
      if (!sectionSets.length) return pts.map(() => null);

      // Best hit PER ROUTE per point (not just the single nearest), so a point
      // can be re-assigned to a neighbouring route by the continuity pass below
      // without another projection round.
      const candidates = pts.map((pt) => {
        const perRoute = [];
        for (const set of sectionSets) {
          const hit = nearestOnSections(set.sections, pt);
          if (hit) perRoute.push({ key: set.key, hit, routeLength: set.routeLength });
        }
        return perRoute;
      });

      // Where the lane CROSSES another route, that route is momentarily the
      // nearest one and a few points would otherwise be labelled with its road
      // and its unrelated milepost. Points are ordered along the lane, so a real
      // road change is a long run and an intersection is a short one.
      const chosen = candidates.map((perRoute) => pickCandidate(perRoute));
      const smoothed = smoothRouteRuns(chosen.map((c) => c?.key ?? null));

      return candidates.map((perRoute, i) => {
        const want = smoothed[i];
        const reassigned = want != null && want !== (chosen[i]?.key ?? null)
          ? perRoute.find((c) => c.key === want)
          : null;
        const pick = reassigned ?? chosen[i];
        if (!pick) return null;
        const loc = locationFromHit(pick.hit, pick.routeLength);
        return {
          road: loc.routeLabel,
          milepost: loc.postedMilepost ?? loc.measure,
          lrs_measure: loc.measure,
          county: loc.zone?.county ?? null,
          region: loc.zone?.region ?? null,
          distance_m: Math.round(loc.distanceMeters * 10) / 10,
        };
      });
    },
  };
}

// ─── Search WHERE-builder ─────────────────────────────────────────────────────

/** Builds the local-search WHERE clause. Recognises a route designation
 *  ("SR2", "I 93"), a municipality name, a bare street name, or a combination
 *  ("Main Street Cambridge"). Exported for testing. */
export function buildLocalWhere(term) {
  const cleaned = String(term ?? '').trim();
  if (!cleaned) return '1=1';

  const clauses = [];
  let working = cleaned;

  // Municipality first — many are multi-word ("North Adams"), so match the
  // longest name present before tokenizing whatever is left.
  const cityEntries = Object.entries(MA_CITY_NAMES)
    .map(([code, name]) => [code, name])
    .sort((a, b) => b[1].length - a[1].length);
  for (const [code, name] of cityEntries) {
    const re = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(working)) {
      clauses.push(`City = ${Number(code)}`);
      working = working.replace(re, ' ');
      break; // one municipality filter per search
    }
  }

  const rest = working.trim();
  if (rest) {
    // "SR2" / "I 93" / "US 20" → a route designation.
    const designation = rest.match(/^(I|US|SR)[\s-]?(\d+[A-Z]?)$/i);
    if (designation) {
      clauses.push(`Route_System = '${designation[1].toUpperCase()}'`);
      clauses.push(`Route_Number = '${sqlLiteral(designation[2].toUpperCase())}'`);
    } else {
      clauses.push(`UPPER(St_Name) LIKE '%${sqlLiteral(rest.toUpperCase())}%'`);
    }
  }

  return clauses.length ? clauses.join(' AND ') : '1=1';
}

export const massdotAdapter = createMassdotAdapter();
